/* eslint-disable import/order */
import React, { useEffect, useRef, useState } from 'react';
import './_CashierHeader.sass';
import { shallowEqual, useSelector } from 'react-redux';
import ProfileModal from 'shared-components/dependent-components/modals/profile-modal/profile-modal.js';
import TransactionsModal from 'shared-components/dependent-components/modals/transactions-modal/transactions-modal';
import logoVirtual from 'shared-components/dependent-components/header/img/logo.svg';
import { config as appConfig } from 'config/config';
import { withRouter } from 'react-router';
import compose from 'utils/helper-functions/compose';
import { withTemplate } from 'shared-components/hoc-components/hoc-context/with-template';
import { withI18N } from 'shared-components/hoc-components';
import { isMobile } from 'utils/detect-devices/is-mobile';
import MyInfo from 'shared-components/dependent-components/header/my-info/my-info';
import Greeting from 'shared-components/dependent-components/header/greeting/greeting';
import { withAdr } from 'shared-components/hoc-components/with-adr';
import { withCurrentPage } from 'shared-components/hoc-components/with-current-page';
import { ArrowDown } from '@cashier-project/assets/images/arrow-down';
import { eNumber } from 'utils/round-functions/e-number';
import { clientClickEvent } from 'utils/custom-cashier-events/client-click-event';
import { isPanoramicTheme } from '@pano-project/variables/is-panoramic-2-0-theme';
import BackToCardMethod from 'shared-components/dependent-components/back-to-card-method/back-to-card-method';
import useBackToCardButton from 'shared-components/dependent-components/back-to-card-method/use-back-tocard-button';
import TransactionTabsHeader from '@pano-project/components/TransactionTabsHeader/TransactionTabsHeader';
import HeaderIconButton from '@pano-project/components/HeaderButtons/HeaderIconButton';
import { debounce } from 'utils/helper-functions/debounce';
import { usePanoramicHeaderBack } from '@pano-project/features/panoramic-billing-page/hooks/usePanoramicHeaderBack';
import { useCashierHeaderEvents } from '@pano-project/hooks/useCashierHeaderEvents';
import { useHeaderState, CashierHeaderConfig } from './HeaderContext';
import { useTransactionTabs } from '@pano-project/hooks/useTransactionTabs';
import { useBalanceLimit } from '@pano-project/hooks/useBalanceLimit';
import { resolveHeaderButtons } from '@pano-project/variables/header-buttons';
import { useHeaderElements } from './useHeaderElements';
import {
  TAB_LABEL_KEY,
  getPrimaryIntent,
  getSecondaryIntents,
  mapIntentToTab,
} from '@pano-project/utils/intentMapping';
import { hasTriedToSubmit } from '@pano-project/utils/submissionAttempt';

const logo = appConfig.logo;

interface CashierHeaderProps {
  history?: any;
  template?: any;
  lang?: (key: string) => string;
  langBack?: (key: string) => string;
  s?: (key: string) => string;
  currentPage?: string;
  config?: CashierHeaderConfig;
}

const CashierHeader = ({
  history,
  template,
  lang,
  langBack,
  s,
  currentPage,
  config: configProp,
}: CashierHeaderProps) => {
  const { goBackToCard } = useBackToCardButton();
  const { handleBack: handlePanoramicBack } = usePanoramicHeaderBack();
  const contextConfig = useHeaderState();

  const containerRef = useRef<HTMLElement | null>(null);
  const backButtonRef = useRef<HTMLButtonElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const myInfoRef = useRef<HTMLDivElement | null>(null);
  const greetingRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const [isTwoRowsHeader, setIsTwoRowsHeader] = useState(false);
  const [greetingMaxWidth, setGreetingMaxWidth] = useState<number | null>(null);
  const [billingModal, setBillingModal] = useState(false);
  const [transactionsModal, setTransactionsModal] = useState(false);

  const {
    resultOfPay,
    type,
    session_type,
    base_amount,
    base_currency,
    show_back_to_card,
    header_style,
    showBackButtonSetting,
    showCloseButtonSetting,
    layout,
    primaryIntent,
    secondaryIntents,
    submissionAttempted,
  } = useSelector((state: any) => {
    const profileInfo = state.profileInfo || {};
    const initTheme = state.initTheme;
    const cs = initTheme?.theme_editor_settings?.customization_settings;
    const profileBaseCurrency = profileInfo.base_currency;
    return {
      resultOfPay: state.resultOfPay,
      type: state.resultOfPay?.transaction_type || profileInfo.transaction_type,
      session_type: state.resultOfPay?.session_type || profileInfo.session_type,
      base_amount: profileInfo.base_amount,
      base_currency: profileBaseCurrency,
      show_back_to_card: state.cardToObSettings?.show_back_to_card,
      header_style: cs?.header_style,
      showBackButtonSetting: cs?.show_back_button,
      showCloseButtonSetting: cs?.show_close_button,
      layout: cs?.layout,
      primaryIntent: getPrimaryIntent(state),
      secondaryIntents: getSecondaryIntents(state),
      submissionAttempted: hasTriedToSubmit(state),
    };
  }, shallowEqual);

  const { showBackButton, showCloseButton, showAnyHeaderButton } = resolveHeaderButtons({
    showBackButton: showBackButtonSetting,
    showCloseButton: showCloseButtonSetting,
    layout,
    headerStyle: header_style,
  });

  const isModernHeader = header_style === 'modern';
  const isAmountFirst = layout === 'amount-first';

  const { notifyClose } = useCashierHeaderEvents();
  // The switch appears only where this header already shows the tabs variant,
  // and only for a multi-intent session; a single-intent session keeps today's
  // non-interactive single tab.
  const { activeTab, isMultiIntent, tabsDisabled, onTabClick: switchTab } = useTransactionTabs();
  // The balance turns to the error colour while a balance rule is broken,
  // worked out from the same state the amount area reads. The amount-first
  // layout sends this fact through its own header configuration instead.
  const { balanceErrorType } = useBalanceLimit();

  // The switch is offered only before a submission. After one, the tab bar and
  // the classic labels show the player's own tab only, not clickable; that rule
  // is applied where they are drawn (see hasTriedToSubmit).
  const onTabClick = switchTab;

  // The classic header's two transaction labels, primary intent first. Two
  // intents that collapse into the same tab (payment and authorization) fall
  // back to the single title below, exactly like the tab header does.
  const classicHeaderTabs = isMultiIntent
    ? [primaryIntent, ...secondaryIntents]
        .map(mapIntentToTab)
        .filter((tab: string, index: number, tabs: string[]) => tabs.indexOf(tab) === index)
    : [];
  const showTransactionLabels = isPanoramicTheme && classicHeaderTabs.length > 1;
  const headerConfigForFlags = configProp ?? contextConfig;
  // The list is decided on all tabs, so the header keeps its look; only the
  // labels shown and their click handlers narrow.
  // A page may replace the back action of the ordinary header (the panoramic
  // billing step goes back to the payment screen).
  const pageBack =
    headerConfigForFlags?.variant === 'default' ? headerConfigForFlags.onBack : undefined;
  const labelsLocked = submissionAttempted || !!headerConfigForFlags?.submitLocked;
  const visibleClassicTabs = labelsLocked
    ? classicHeaderTabs.filter((tab: string) => tab === activeTab)
    : classicHeaderTabs;

  const handleLabelKeyDown = (tab: string) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTabClick?.(tab);
    }
  };

  const reducedHeaderModeByPage: Record<string, 'close-only' | 'back-and-close'> = {
    resultPage: 'close-only',
    errorPage: 'back-and-close',
    redirectPage: 'back-and-close',
    redirectEmbeddedPage: 'back-and-close',
  };
  const activeConfig = configProp ?? contextConfig;
  const isSecondaryStep =
    activeConfig?.variant === 'top-bar' || activeConfig?.variant === 'title-row';

  const {
    showPaymentType,
    showBalance,
    balance: formattedBalance,
  } = useHeaderElements({
    currentPage,
    isSecondaryStep,
    ignorePageRestriction: activeConfig?.showHeaderElements === true,
  });

  const showHeaderElements = showPaymentType || showBalance;

  const usesTabsHeader = isPanoramicTheme && (isModernHeader || isAmountFirst);
  const tabsHeaderIsEmpty =
    usesTabsHeader && !showPaymentType && !formattedBalance && !showBackButton && !showCloseButton;

  const reducedHeaderMode =
    showAnyHeaderButton && !showHeaderElements
      ? reducedHeaderModeByPage[currentPage as string]
      : undefined;

  let pageTitle = '';
  switch (type) {
    case 'pay-in':
      pageTitle = base_amount
        ? `${lang!('pay')} ${eNumber(base_amount)} ${base_currency}`
        : `${lang!('deposit')}`;
      break;
    case 'pay-out':
      pageTitle = base_amount
        ? `${lang!('withdraw')} ${eNumber(base_amount)} ${base_currency}`
        : `${lang!('withdrawal')}`;
      break;
    default:
      pageTitle = '';
  }

  const handleBack = () => {
    clientClickEvent('back_button');
    history?.goBack();
  };

  const headerLogo = (
    <div className={`${s!('header-logo')}`} ref={headerRef}>
      {!isPanoramicTheme &&
      currentPage === 'paymentPage' &&
      !(['slider', 'beside'].includes(template?.sliderMethods) && !isMobile) ? (
        <div
          className={`${s!('back')}`}
          onClick={() => {
            if (show_back_to_card) {
              goBackToCard({ callback: handleBack });
            } else {
              handleBack();
            }
          }}
        >
          <span className={`${s!('arrow-wrapper')}`}>
            <ArrowDown />
          </span>
          <span>{lang!('back')}</span>
        </div>
      ) : (
        <div className={`${s!('logo-container')}`}>
          {logo ? <img alt={''} src={session_type === 'vt' ? logoVirtual : logo} /> : ''}
          {show_back_to_card && isPanoramicTheme ? (
            // The decline-recovery control already takes the title's place today;
            // it keeps doing so even for a multi-intent session, so the two
            // transaction labels never appear together with it.
            <BackToCardMethod />
          ) : showTransactionLabels ? (
            <div className={`${s!('classic-header-tabs')}`} role="tablist">
              {visibleClassicTabs.map((tab: string) => {
                const isActiveLabel = tab === activeTab;
                return (
                  <span
                    key={tab}
                    role="tab"
                    aria-selected={isActiveLabel}
                    className={`${s!('classic-header-tab')} ${isActiveLabel ? '_active' : '_inactive'}${
                      !labelsLocked && tabsDisabled ? ' _disabled' : ''
                    }`}
                    {...(!labelsLocked &&
                      !tabsDisabled && {
                        tabIndex: 0,
                        onClick: () => onTabClick?.(tab),
                        onKeyDown: handleLabelKeyDown(tab),
                      })}
                    {...(!labelsLocked && tabsDisabled && { 'aria-disabled': true })}
                  >
                    {lang!(TAB_LABEL_KEY[tab])}
                  </span>
                );
              })}
            </div>
          ) : (
            <div className={`${s!('main-method')}`}>{pageTitle}</div>
          )}
        </div>
      )}
    </div>
  );

  useEffect(() => {
    if (!showAnyHeaderButton) return undefined;

    const offset = 32;
    const containerElement = containerRef?.current;
    const backButtonElement = backButtonRef?.current;
    const headerElement = headerRef?.current;
    const myInfoElement = myInfoRef?.current;
    const greetingElement = greetingRef?.current;
    const closeButtonElement = closeButtonRef?.current;

    const checkElementsWidth = debounce(() => {
      const containerWidth = containerElement?.offsetWidth || 0;
      const backButtonWidth = backButtonElement?.offsetWidth || 0;
      const headerWidth = headerElement?.offsetWidth || 0;
      const myInfoWidth = myInfoElement?.offsetWidth || 0;
      const greetingWidth = greetingElement?.offsetWidth || 0;
      const closeButtonWidth = closeButtonElement?.offsetWidth || 0;

      const contentWidth =
        backButtonWidth +
        backButtonWidth +
        headerWidth +
        myInfoWidth +
        greetingWidth +
        closeButtonWidth;

      setIsTwoRowsHeader(!!containerWidth && containerWidth < contentWidth + offset);
      setGreetingMaxWidth(
        containerWidth -
          (headerWidth +
            myInfoWidth +
            (greetingRef.current?.firstChild as HTMLElement)?.offsetWidth +
            offset),
      );
    }, 50);

    const resizeObserver = new ResizeObserver(() => {
      checkElementsWidth();
    });

    containerElement && resizeObserver.observe(containerElement);
    backButtonElement && resizeObserver.observe(backButtonElement);
    headerElement && resizeObserver.observe(headerElement);
    myInfoElement && resizeObserver.observe(myInfoElement);
    greetingElement && resizeObserver.observe(greetingElement);
    closeButtonElement && resizeObserver.observe(closeButtonElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isTwoRowsHeader, showAnyHeaderButton]);

  const handleBackButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (pageBack) {
      pageBack();
      return;
    }
    handlePanoramicBack(currentPage === 'redirectEmbeddedPage');
  };

  const handleCloseButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    notifyClose();
  };

  const renderBackButton = (onClick: (e: React.MouseEvent) => void = handleBackButtonClick) => (
    <HeaderIconButton
      variant="back"
      buttonRef={backButtonRef}
      onClick={onClick}
      className="trx-tabs__button _old-header-variant"
    />
  );

  const renderCloseButton = () => (
    <HeaderIconButton
      variant="close"
      buttonRef={closeButtonRef}
      onClick={handleCloseButtonClick}
      className="trx-tabs__button _old-header-variant"
    />
  );

  const renderTopBar = ({
    showBack,
    showClose,
    onBack,
    closeOnly = false,
    showErrorInfo = false,
    backDisabled = false,
    extraClassName = '',
  }: {
    showBack: boolean;
    showClose: boolean;
    onBack?: () => void;
    closeOnly?: boolean;
    showErrorInfo?: boolean;
    backDisabled?: boolean;
    extraClassName?: string;
  }) =>
    !showBack && !showClose && !showErrorInfo
      ? null
      : renderTabsHeader({
          onBack,
          forceBack: showBack,
          showClose,
          backDisabled,
          hidePaymentType: true,
          extraClassName,
          tabsModifier: closeOnly
            ? 'main-header__tabs--close-only'
            : 'main-header__tabs--buttons-only',
          errorInfo: showErrorInfo ? (
            <div className="main-header__error-info">
              {logo ? <img alt="" src={session_type === 'vt' ? logoVirtual : logo} /> : ''}
              <span className="main-header__base-currency">{base_currency}</span>
              {formattedBalance && <span className="main-header__balance">{formattedBalance}</span>}
            </div>
          ) : undefined,
        });

  const renderTabsHeader = ({
    onBack,
    forceBack,
    showClose,
    backDisabled,
    activeTab,
    onTabClick,
    withPending,
    hidePaymentType = false,
    errorInfo,
    tabsModifier = '',
    extraClassName = '',
    hasBalanceError = false,
    submitLocked,
  }: {
    onBack?: () => void;
    forceBack?: boolean;
    showClose?: boolean;
    backDisabled?: boolean;
    activeTab?: string;
    onTabClick?: (tab: string) => void;
    withPending?: boolean;
    hidePaymentType?: boolean;
    errorInfo?: React.ReactNode;
    tabsModifier?: string;
    extraClassName?: string;
    hasBalanceError?: boolean;
    submitLocked?: boolean;
  }) => {
    const tabs = (
      <TransactionTabsHeader
        transactionType={type}
        lang={(langBack ?? lang) as (key: string) => string}
        activeTab={activeTab}
        onTabClick={onTabClick}
        withPending={withPending}
        className={`main-header__tabs ${tabsModifier}`.trim()}
        balanceTabClassName={type}
        errorInfo={errorInfo}
        showPaymentType={!hidePaymentType && showPaymentType}
        balance={hidePaymentType ? undefined : formattedBalance}
        showBackButton={forceBack}
        showCloseButton={showClose}
        backDisabled={backDisabled}
        onBack={onBack}
        hasBalanceError={hasBalanceError}
        submitLocked={submitLocked}
        tabsDisabled={tabsDisabled}
      />
    );

    return (
      <header
        ref={containerRef}
        className={`${s!('main-header')} ${
          session_type === 'vt' ? s!('header-virtual_terminal') : ''
        } ${showAnyHeaderButton ? '_show-hide-and-back-buttons' : ''} ${extraClassName}`.trim()}
      >
        {tabs}
      </header>
    );
  };

  if (configProp || contextConfig) {
    const cfg = (configProp ?? contextConfig) as CashierHeaderConfig;

    if (cfg.variant === 'none') return null;

    if (cfg.variant === 'tabs') {
      if (!showPaymentType && !formattedBalance && !showBackButton && !showCloseButton) return null;

      return renderTabsHeader({
        activeTab: cfg.activeTab,
        onTabClick: cfg.onTabClick,
        withPending: cfg.withPending,
        forceBack: showBackButton,
        showClose: showCloseButton,
        hasBalanceError: cfg.balanceError,
        submitLocked: cfg.submitLocked,
      });
    }

    if (isSecondaryStep && cfg.titleLayout !== 'stacked') {
      return renderTabsHeader({
        onBack: cfg.onBack,
        forceBack: cfg.showBack ?? true,
        showClose: cfg.showClose,
        backDisabled: cfg.backDisabled,
        // The screen's own switch reaches the tab bar; the payment type
        // setting still decides whether the tabs are drawn here at all.
        activeTab: cfg.activeTab,
        onTabClick: cfg.onTabClick,
        hasBalanceError: cfg.balanceError,
        submitLocked: cfg.submitLocked,
      });
    }

    if (cfg.variant === 'top-bar') {
      const closeVisible = cfg.showClose ?? showCloseButton;
      const backVisible = cfg.showBack ?? true;
      const onClose =
        cfg.onClose ??
        ((e: React.MouseEvent) => {
          e.preventDefault();
          notifyClose();
        });

      return renderTopBar({
        showBack: backVisible,
        showClose: closeVisible,
        onBack: cfg.onBack,
        closeOnly: !backVisible,
        backDisabled: cfg.backDisabled,
        extraClassName: cfg.className ?? '',
      });
    }

    if (cfg.variant === 'title-row') {
      const closeVisible = cfg.showClose ?? showCloseButton;
      const backVisible = cfg.showBack ?? true;
      const onClose =
        cfg.onClose ??
        ((e: React.MouseEvent) => {
          e.preventDefault();
          notifyClose();
        });

      if (cfg.titleLayout === 'stacked') {
        return (
          <>
            <div className={cfg.className ?? 'cashier-header__actions'}>
              {backVisible && (
                <HeaderIconButton
                  variant="back"
                  onClick={cfg.onBack}
                  ariaLabel={(langBack ?? lang)?.('back')}
                />
              )}
              {closeVisible && <HeaderIconButton variant="close" onClick={onClose} />}
            </div>
            <h1 className={cfg.titleClassName ?? 'cashier-header__stacked-title'}>{cfg.title}</h1>
          </>
        );
      }

      return (
        <div className={cfg.className ?? 'cashier-header cashier-header--title-row'}>
          {backVisible && (
            <HeaderIconButton variant="back" onClick={cfg.onBack} disabled={cfg.backDisabled} />
          )}
          <span
            className={`${cfg.titleClassName ?? 'cashier-header__title'}${
              cfg.showStickyTitle ? ' _visible' : ''
            }`}
          >
            {cfg.title}
          </span>
          {closeVisible && <HeaderIconButton variant="close" onClick={onClose} />}
        </div>
      );
    }
  }

  if (resultOfPay && currentPage !== 'errorPage' && !isPanoramicTheme) {
    return (
      <div
        className={`${s!('main-header')} ${session_type === 'vt' ? s!('header-virtual_terminal') : ''}`}
      >
        {headerLogo}
      </div>
    );
  }

  const handleMinimalBackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handlePanoramicBack(true);
  };

  if (reducedHeaderMode) {
    const backAllowedOnPage = reducedHeaderMode !== 'close-only';
    const showBack = backAllowedOnPage && showBackButton;

    return renderTopBar({
      showBack,
      showClose: showCloseButton,
      onBack: () => handlePanoramicBack(true),
      closeOnly: !showBack,
      showErrorInfo: currentPage === 'errorPage',
      extraClassName: 'main-header--reduced',
    });
  }

  const showDefaultHeader =
    (currentPage !== 'errorPage' || showHeaderElements) && !tabsHeaderIsEmpty;

  return (
    <>
      {billingModal && <ProfileModal close={() => setBillingModal(false)} />}
      {transactionsModal && <TransactionsModal close={() => setTransactionsModal(false)} />}

      {showDefaultHeader &&
        (usesTabsHeader ? (
          renderTabsHeader({
            onBack: pageBack ?? (() => handlePanoramicBack(currentPage === 'redirectEmbeddedPage')),
            activeTab,
            onTabClick,
            hasBalanceError: !isAmountFirst && balanceErrorType !== null,
          })
        ) : (
          <>
            {show_back_to_card && !isPanoramicTheme && <BackToCardMethod />}

            {isTwoRowsHeader && (
              <div className="main-header__buttons-wrapper">
                {showBackButton && renderBackButton()}
                {showCloseButton && renderCloseButton()}
              </div>
            )}

            <header
              ref={containerRef}
              className={`${s!('main-header')} ${
                session_type === 'vt' ? s!('header-virtual_terminal') : ''
              } ${showAnyHeaderButton ? '_show-hide-and-back-buttons' : ''}`}
            >
              {showBackButton && !isTwoRowsHeader && renderBackButton()}
              {headerLogo}

              <span className="main-header__base-currency">{base_currency}</span>

              {type === 'pay-out' && session_type !== 'vt' && appConfig.withdrawRequests ? (
                <MyInfo
                  myInfoRef={myInfoRef}
                  setBillingModal={setBillingModal}
                  setTransactionsModal={setTransactionsModal}
                />
              ) : (
                <Greeting
                  greetingMaxWidth={greetingMaxWidth}
                  greetingRef={greetingRef}
                  setBillingModal={setBillingModal}
                />
              )}
              {showCloseButton && !isTwoRowsHeader && renderCloseButton()}
            </header>
          </>
        ))}
    </>
  );
};

// Named export kept alongside the default export so focused tests can render
// the component directly, without its routing/i18n/redux HOC wrappers.
export { CashierHeader };

export default compose(withRouter, withTemplate, withI18N, withAdr, withCurrentPage)(CashierHeader);
